import { ToggleMonitoringDrawer } from '@protector/entities/core/__legacy__/redux.actions';
import { usePreviousValue } from 'beautiful-react-hooks';
import { retryFunction, verifyBalanceAppAccess } from 'core/utils/functions';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { captureError } from 'infra/monitoring/monitoring.utils';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from 'redux/app-state';
import { SvgBlocked } from 'st-protector-icons/Minor/Blocked';
import SvgMapExport from 'st-protector-icons/Minor/ExportMap';
import FieldMonitoring from 'st-protector-icons/Minor/FieldMonitoring';
import { NemaInsightsReport } from 'st-protector-icons/Minor/NemaInsightsReport';
import SvgNemaSamplingPoints from 'st-protector-icons/Minor/NemaSamplingPoints';
import { RainReportSvg } from 'st-protector-icons/Minor/RainReport';
import SvgReport from 'st-protector-icons/Minor/Report';
import SvgReportConsolidated from 'st-protector-icons/Minor/ReportConsolidated';
import SvgReportDetailed from 'st-protector-icons/Minor/ReportDetailed';
import SvgSample from 'st-protector-icons/Minor/Sample';
import SvgSpray from 'st-protector-icons/Minor/Spray';
import SvgStaticPointReport from 'st-protector-icons/Minor/StaticPointReport';
import { ReactComponent as SvgPinSamplePoints } from 'st-protector-icons/Minor/svg-pin-sample-points.svg';
import SvgPinSample from 'st-protector-icons/Minor/SvgPinSample';
import SVGSeason from 'st-protector-icons/Minor/SvgSeason';
import SvgTableChart from 'st-protector-icons/Minor/TableChart';
import SvgWarehouseReport from 'st-protector-icons/Minor/WarehouseReport';
import { LoadingSpinner } from './drawers/components/loading-spinner.component';
import { ReportDrawerUnavailable } from './report-drawer-unavailable.component';
import { ReportsTypes } from './reports-types.enum';

// Bounded to ~3s worst case (click-time), unlike the topbar's passive 5-attempt/5s budget.
const CHUNK_RETRY_ATTEMPTS = 3;
const CHUNK_RETRY_DELAY_MS = 1000;

const reportChunkFailure = (message: string) => (error: unknown) =>
  captureError(error, { message, source: 'custom', domain: 'ReportDrawer' });

// React caches a rejected `React.lazy` import for the session (P40-62105), so retry
// first and fall back to an actionable drawer instead of blanking the app via the root
// error boundary. Every rejection is treated the same and still reported with the real
// error, since import() rejections don't expose a discriminable error code to split on.
export const lazyWithChunkRetry = <T,>(importer: () => Promise<{ default: T }>, failureMessage: string) =>
  React.lazy(() =>
    retryFunction(importer, CHUNK_RETRY_ATTEMPTS, CHUNK_RETRY_DELAY_MS).catch(error => {
      reportChunkFailure(failureMessage)(error);
      return { default: ReportDrawerUnavailable as unknown as T };
    })
  );

const STReportSprayDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-spray.component'),
  'Report spray drawer chunk failed to load'
);
const STReportSamplesDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-sample.component'),
  'Report sample drawer chunk failed to load'
);
const STReportRainGaugeDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-rain-gauge.component'),
  'Report rain gauge drawer chunk failed to load'
);
const STImportSampling = lazyWithChunkRetry(
  () => import('./drawers/import-sampling.component'),
  'Import sampling drawer chunk failed to load'
);
const STReportTrapWarehouse = lazyWithChunkRetry(
  () => import('./drawers/report-trap-warehouse.component'),
  'Report trap warehouse drawer chunk failed to load'
);
const STReportScoutingDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-scouting.component'),
  'Report scouting drawer chunk failed to load'
);
const STReportPropertyDrawer = lazyWithChunkRetry(
  () => import('core/components/header/components/report/drawers/report-property.component'),
  'Report property drawer chunk failed to load'
);
const STTrackingExportDrawer = lazyWithChunkRetry(
  () => import('core/components/header/components/report/drawers/report-property.tracking-export.component'),
  'Report tracking export drawer chunk failed to load'
);
const STReportDetailedConsolidatedDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-detailed-consolidated.component'),
  'Report detailed consolidated drawer chunk failed to load'
);
const STReportVisitDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-visit.component'),
  'Report visit drawer chunk failed to load'
);
const ReportDiseaseRiskSelectOptions = lazyWithChunkRetry(
  () => import('./drawers/report-disease-risk-select-options'),
  'Report disease risk select options chunk failed to load'
);
const ReportControlStrategy = lazyWithChunkRetry(
  () => import('./drawers/report-control-strategy.component'),
  'Report control strategy drawer chunk failed to load'
);
const ReportBlockedFields = lazyWithChunkRetry(
  () => import('./drawers/report-blocked-fields.component'),
  'Report blocked fields drawer chunk failed to load'
);
const ReportNemaSamplingPointsDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-nema-sampling-points.component'),
  'Report nema sampling points drawer chunk failed to load'
);
const ReportNemaInsightsDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-nema-insights.component'),
  'Report nema insights drawer chunk failed to load'
);
const SeasonReport = lazyWithChunkRetry(
  () => import('../season-report/season-report.component').then(({ SeasonReport }) => ({ default: SeasonReport })),
  'Season report chunk failed to load'
);
const STReportTrendDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-trend.component'),
  'Report trend drawer chunk failed to load'
);
const STReportOnlineDrawer = lazyWithChunkRetry(
  () => import('./drawers/report-online.component'),
  'Report online drawer chunk failed to load'
);

const importReportDrawerListContainer = () => import('./report-drawer-list.container');

// No retryFunction here: opening the drawer is the user's first interaction, so it
// fails fast (P40-62105) instead of stalling on a permanent failure.
const ReportDrawerListContainer = React.lazy(() =>
  importReportDrawerListContainer().catch(error => {
    reportChunkFailure('Report drawer list chunk failed to load on open')(error);
    return { default: ReportDrawerUnavailable };
  })
);

const preloadReportDrawerChunksOnModuleLoad = () => {
  void importReportDrawerListContainer().catch(reportChunkFailure('Report drawer list chunk preload failed'));
};

preloadReportDrawerChunksOnModuleLoad();
const NotesReport = lazyWithChunkRetry(
  () =>
    import('core/components/header/components/report/notes-report/notes-report.component').then(({ NotesReport }) => ({
      default: NotesReport
    })),
  'Notes report chunk failed to load'
);

// Exposed for tests only, to exercise each chunk's import + retry wiring directly.
export const reportDrawerLazyComponentsForTesting = {
  STReportSprayDrawer,
  STReportSamplesDrawer,
  STReportRainGaugeDrawer,
  STImportSampling,
  STReportTrapWarehouse,
  STReportScoutingDrawer,
  STReportPropertyDrawer,
  STTrackingExportDrawer,
  STReportDetailedConsolidatedDrawer,
  STReportVisitDrawer,
  ReportDiseaseRiskSelectOptions,
  ReportControlStrategy,
  ReportBlockedFields,
  ReportNemaSamplingPointsDrawer,
  ReportNemaInsightsDrawer,
  SeasonReport,
  STReportTrendDrawer,
  STReportOnlineDrawer,
  NotesReport
};

const STReportDrawerRender = () => {
  const segmentTracking = useSegmentTracking();
  const dispatch = useDispatch();
  const [reportToOpen, setReportToOpen] = useState<ReportsTypes | null>(null);
  const licensingStatus = useSelector((state: AppState) => state.entities.company.licensingStatus);

  const visible = useSelector((state: AppState) => state.uiState.global.layoutState.showMonitoringDrawer);

  const hasBalanceApp = useMemo(() => verifyBalanceAppAccess(licensingStatus), [licensingStatus]);
  const canAccessInventoryReport = useMemo(() => {
    return !hasBalanceApp && reportToOpen === ReportsTypes.WAREHOUSE_INVENTORY;
  }, [hasBalanceApp, reportToOpen]);

  const callbackDrawer = useCallback(() => {
    dispatch(ToggleMonitoringDrawer());
  }, [dispatch]);

  const previousReportType = usePreviousValue(reportToOpen);

  useEffect(() => {
    if (!reportToOpen || reportToOpen === previousReportType) return;

    if (reportToOpen === ReportsTypes.DISEASE_RISK) {
      segmentTracking.track('Reports - Export Report - Selected disease risk report');
      return;
    }

    segmentTracking.track('Export Report - Selected report type', { reportType: reportToOpen });
  }, [reportToOpen, segmentTracking, previousReportType]);

  const closeDrawer = () => {
    segmentTracking.track('Export Report - Closed Export Report drawer', { reportType: reportToOpen ?? '' });
    callbackDrawer();
  };

  const closeDrawerReport = () => {
    setReportToOpen(null);
  };

  return (
    <Suspense fallback={<LoadingSpinner />}>
      {reportToOpen === ReportsTypes.SPRAY && <STReportSprayDrawer callbackDrawer={closeDrawerReport} icon={<SvgSpray fill='#01658d' />} />}

      {(reportToOpen === ReportsTypes.SAMPLE || reportToOpen === ReportsTypes.SAMPLING) && (
        <STReportSamplesDrawer
          callbackDrawer={closeDrawerReport}
          type={reportToOpen}
          icon={reportToOpen === ReportsTypes.SAMPLE ? <SvgPinSample fill='#705BC1' /> : <SvgPinSamplePoints />}
        />
      )}

      {reportToOpen === ReportsTypes.RAIN_GAUGE_REPORT && (
        <STReportRainGaugeDrawer icon={<RainReportSvg />} callbackDrawer={closeDrawerReport} />
      )}

      {reportToOpen === ReportsTypes.SAMPLING_IMPORT && (
        <STImportSampling callbackDrawer={closeDrawerReport} type={ReportsTypes.SAMPLING_IMPORT} icon={<SvgPinSamplePoints />} />
      )}

      {(reportToOpen === ReportsTypes.TRAP || canAccessInventoryReport) && (
        <STReportTrapWarehouse
          callbackDrawer={closeDrawerReport}
          type={reportToOpen}
          icon={reportToOpen === ReportsTypes.TRAP ? <SvgStaticPointReport fill='#D9800A' /> : <SvgWarehouseReport fill='#1A7AA3' />}
        />
      )}

      {reportToOpen === ReportsTypes.SCOUTING && (
        <STReportScoutingDrawer callbackDrawer={closeDrawerReport} icon={<SvgSample fill='#f74141' />} />
      )}

      {reportToOpen === ReportsTypes.PROPERTY && (
        <STReportPropertyDrawer callbackDrawer={closeDrawerReport} icon={<SvgReport fill='#198746' />} />
      )}

      {reportToOpen === ReportsTypes.TRACKING && <STTrackingExportDrawer callbackDrawer={closeDrawerReport} icon={<SvgMapExport />} />}

      {(reportToOpen === ReportsTypes.MONITORING_DETAILED || reportToOpen === ReportsTypes.MONITORING_CONSOLIDATED) && (
        <STReportDetailedConsolidatedDrawer
          reportType={reportToOpen}
          callbackDrawer={closeDrawerReport}
          icon={reportToOpen === ReportsTypes.MONITORING_DETAILED ? <SvgReportDetailed /> : <SvgReportConsolidated />}
        />
      )}

      {reportToOpen === ReportsTypes.VISIT && <STReportVisitDrawer callbackDrawer={closeDrawerReport} icon={<FieldMonitoring />} />}

      {reportToOpen === ReportsTypes.DISEASE_RISK && (
        <ReportDiseaseRiskSelectOptions onClose={closeDrawerReport} closeMainDrawer={closeDrawer} />
      )}

      {reportToOpen === ReportsTypes.CONTROL_STRATEGIES && (
        <ReportControlStrategy callbackDrawer={closeDrawerReport} icon={<SvgTableChart fill='#0071CD' />} />
      )}

      {reportToOpen === ReportsTypes.BLOCKED_FIELDS && <ReportBlockedFields callbackDrawer={closeDrawerReport} icon={<SvgBlocked />} />}

      {reportToOpen === ReportsTypes.NEMA_SAMPLING_POINTS && (
        <ReportNemaSamplingPointsDrawer callbackDrawer={closeDrawerReport} icon={<SvgNemaSamplingPoints />} />
      )}

      {reportToOpen === ReportsTypes.NEMA_INSIGHTS && (
        <ReportNemaInsightsDrawer callbackDrawer={closeDrawerReport} icon={<NemaInsightsReport />} />
      )}

      {reportToOpen === ReportsTypes.SEASON_REPORT && (
        <SeasonReport reportType={reportToOpen} callbackDrawer={closeDrawerReport} icon={<SVGSeason />} />
      )}

      {reportToOpen === ReportsTypes.TREND_REPORT && <STReportTrendDrawer callbackDrawer={closeDrawerReport} />}

      {reportToOpen === ReportsTypes.ONLINE_REPORT && <STReportOnlineDrawer callbackDrawer={closeDrawerReport} />}

      {reportToOpen === ReportsTypes.NOTES && <NotesReport onClose={closeDrawerReport} />}

      {visible && (
        <ReportDrawerListContainer
          callbackDrawer={callbackDrawer}
          closeDrawer={closeDrawer}
          reportToOpen={reportToOpen}
          setReportToOpen={setReportToOpen}
        />
      )}
    </Suspense>
  );
};

export default STReportDrawerRender;
